import { BaseSyntheticEvent, useEffect, useState, useContext } from "react";
import { TextField, Grid, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm, SubmitHandler, Controller, Control, FieldErrorsImpl } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from '@hookform/resolvers/yup';
import { CenterBox, TitleDiv, OnsiteProgress } from "../../customelements/CustomInput";
import { PostCodeValidation, AddressValidation, CustomerNameValidation, PhoneNumberValidation, BikoValidation, QuantityValidation } from "../../common/Valitation";
import { HttpPostData, JumpPage, CvNull } from "../../common/Util";
import { TitleParamContext } from "../../context/TitleContext";
import { ProductMaster, NewProductParam, LicenseDatas, ToPaymentConfirm, ResponseParamBase } from "../../common/Types";
import market from "../../css/market.module.scss";
import base from "../../css/base.module.css";


export interface IFormInputs {
    quantity: number,
    categoryCode: string,
    productCode: string,
    postCode: string,
    address: string,
    customerName: string,
    phoneNumber: string,
    biko: string,
}

export type ResponseParam = ResponseParamBase & {
    data: ProductMaster
}

export type ResponseParam2 = ResponseParamBase & {
    data: LicenseDatas,
    deliverySpan: number,
    test: string,
}

const schema = Yup.object({
    quantity: QuantityValidation(1, 999),
}).required();

/*const addressSchema = Yup.object({
    quantity: licenseQuantityValidation,
    postCode: PostCodeValidation,
    address: AddressValidation,
    customerName: CustomerNameValidation,
    phoneNumber: PhoneNumberValidation,
    biko: BikoValidation,
}).required();*/

const addressSchema = (minValue: number, maxValue: number) => {
    return Yup.object({
        quantity: QuantityValidation(minValue, maxValue),
        postCode: PostCodeValidation,
        address: AddressValidation,
        customerName: CustomerNameValidation,
        phoneNumber: PhoneNumberValidation,
        biko: BikoValidation,
    }).required();
}

export const NewLicense = () => {
    const location = useLocation();
    const titleContext = useContext(TitleParamContext);
    const navigate = useNavigate();
    const beforePageParam: NewProductParam = location.state as NewProductParam;
    const [productData, setProductData] = useState<ResponseParam>();
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const { handleSubmit, trigger, control, getValues, setValue, watch, formState: { errors, isValid } } = useForm<IFormInputs>({
        defaultValues: {
            quantity: 1,
            postCode: "",
            address: "",
            phoneNumber: "",
            customerName: "",
            biko: ""
        },
        resolver: yupResolver(productData?.data.isDelivery ? addressSchema(1, productData?.data.inventory + productData?.data.InventoryExceedSize) : schema),
        mode: "onChange",
        reValidateMode: "onChange",
    });

    useEffect(() => {
        (async () => {
            titleContext.setParamData({ mainTitle: "サポート・製品購入", firstSubTitle: "新規ライセンス購入", secondSubTitle: "" });
            await HttpPostData<ResponseParam>("/License/GetProductData", { ProductCode: beforePageParam.productCode, CategoryCode: beforePageParam.categoryCode }, setIsLoading)
                .then((response) => {
                    if (response.data.resultFlg) {
                        setProductData(response.data);
                    }
                })
                .catch((e) => {
                    console.log(e);
                })
                .finally(() => {
                });
        })()
    }, [])

    const onSubmit: SubmitHandler<IFormInputs> = async (data: IFormInputs, event?: BaseSyntheticEvent) => {
        await HttpPostData<ResponseParam2>("/License/GetBuyNewLicenseData", [
            { quantity: data.quantity, categoryCode: beforePageParam.categoryCode, productCode: CvNull(productData?.data.code), postCode: data.postCode, address: data.address, customerName: data.customerName, phoneNumber: data.phoneNumber, biko: data.biko, isNewAgreement: beforePageParam.paymentPageType }
        ], setIsLoading)
            .then((response) => {
                return response.data;
            })
            .then((result) => {
                JumpPage<ToPaymentConfirm>("PaymentConfirm",
                    {
                        productParam: { quantity: data.quantity, categoryCode: beforePageParam.categoryCode, productCode: CvNull(productData?.data.code), productName: CvNull(productData?.data.name), nonTaxPrice: productData?.data.nonTaxPrice ?? 0, taxPrice: productData?.data.taxPrice ?? 0, includeTaxPrice: productData?.data.taxPrice ?? 0, postCode: data.postCode, address: data.address, customerName: data.customerName, phoneNumber: data.phoneNumber, biko: data.biko, paymentPageType: beforePageParam.paymentPageType, isDelivery: productData?.data.isDelivery === void 0 ? false : productData?.data.isDelivery },
                        purchaseParam: result.data,
                        deliverySpan: result.deliverySpan,
                        test: result.test,
                    }
                    , "購入確認", navigate);
            })
            .catch(e => {
                return { userLicenses: [], totalIncludeTaxPrice: 0, totalNonTaxPrice: 0, totalTaxPrice: 0 }
            });
    }

    return (
        <>
            {isLoading ?
                <CenterBox><OnsiteProgress /></CenterBox>
                :
                <section className={market.marketArea}>
                    <div className={market.marketAreaInner}>
                        <form onSubmit={(e) => { handleSubmit(onSubmit)(e) }}>
                            <div className={base.flexCenterColumn}>
                                <CenterBox sx={{ fontSize: 20 }}>{productData?.data.explanation}</CenterBox>
                                {/*productData?.data.isDelivery &&
                                        <CenterBox sx={{ fontSize: 25 }}>{productData?.data.isDelivery ? "(在庫:" + productData?.data.inventory + "個)" : ""}</CenterBox>
                                */}
                                <div className={market["layoutBox-l"]} style={{ alignItems: "center", justifyContent: "flex-start" }}>
                                    <div className={market.marketInputTitle} style={{ width: "60px" }}>
                                        <h3>数量</h3>
                                    </div>
                                    <div className={market.marketInputTitleContents}>
                                        <Controller
                                            name="quantity"
                                            control={control}
                                            render={({ field, fieldState }) => (
                                                <FormControl>
                                                    <InputLabel id="area-label"></InputLabel>
                                                    <Select
                                                        labelId="area-label"
                                                        {...field}
                                                    >
                                                        {
                                                            (function () {
                                                                const list: JSX.Element[] = [];
                                                                for (let i = 1; i <= (productData?.data.isDelivery ? /*(productData?.data.inventory + productData?.data.InventoryExceedSize)*/10 : 999); i++) {
                                                                    list.push(<MenuItem value={i}>{i}</MenuItem>);
                                                                }
                                                                return list;
                                                            }())
                                                        },
                                                    </Select>
                                                </FormControl>
                                            )}
                                        />
                                    </div>
                                </div>
                            </div>
                            {productData?.data.isDelivery && <AddressForm control={control} errors={errors} />}

                            <div className={market.marketFooterArea}>
                                <button className={market.marketBackButton} type="button" onClick={(e) => { JumpPage("/MyPage", {}, "", navigate); }}>戻る</button>
                                <button className={market.marketButton} type="submit" disabled={!isValid}>購入手続き</button>
                                <button style={{ visibility: "hidden" }} className={market.marketBackButton}>＜　進む</button>
                            </div>
                        </form>
                    </div>
                </section>
            }
        </>
    );
}

type AddresFormParam = {
    control: Control<IFormInputs>,
    errors: FieldErrorsImpl<IFormInputs>
}

const AddressForm = (props: AddresFormParam) => {

    return (
        <>
            <TitleDiv title="発送先" contents="（ヤマト運輸での配送可能な日本国内のみ。　サービス提供不可エリアはこちら。　）"></TitleDiv>
            <Grid container spacing={3} alignItems="center" justifyContent="center" direction="column" margin="auto">
                <Grid item xs={1}>
                    <Controller
                        name="postCode"
                        control={props.control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                error={props.errors.postCode ? true : false}
                                margin="normal"
                                type="text"
                                helperText={props.errors.postCode?.message}
                                color="secondary"
                                label="郵便番号"
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={1}>
                    <Controller
                        name="address"
                        control={props.control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                error={props.errors.address ? true : false}
                                margin="normal"
                                type="text"
                                helperText={props.errors.address?.message}
                                color="secondary"
                                label="住所"
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={1}>
                    <Controller
                        name="customerName"
                        control={props.control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                error={props.errors.customerName ? true : false}
                                margin="normal"
                                type="text"
                                helperText={props.errors.customerName?.message}
                                color="secondary"
                                label="名前"
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={1}>
                    <Controller
                        name="phoneNumber"
                        control={props.control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                error={props.errors.phoneNumber ? true : false}
                                margin="normal"
                                type="tel"
                                helperText={props.errors.phoneNumber?.message}
                                color="secondary"
                                label="電話番号"
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={8}>
                    <Controller
                        name="biko"
                        control={props.control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                error={props.errors.biko ? true : false}
                                margin="normal"
                                type="number"
                                helperText={props.errors.biko?.message}
                                color="secondary"
                                multiline={true}
                                rows={4}
                                label="備考"
                            />
                        )}
                    />
                </Grid>
            </Grid>
        </>
    );
}

